import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  getPosOrder,
  listBranches,
  listReleasing,
  releaseOrder,
  voidOrder,
  type ReleasingOrder,
} from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { BranchPicker } from '../components/branch-picker';
import { Receipt } from '@mlm/ui';
import { orderToReceipt, type ReceiptData } from '@mlm/shared';
import { prettyError } from '../lib/pos-errors';

function extractError(err: unknown): string {
  const data = (
    err as { response?: { data?: { error?: string; message?: string; detail?: string } } }
  )?.response?.data;
  return data?.error ?? data?.detail ?? data?.message ?? 'error';
}

/**
 * BF3 — the Releasing area. Lists POS orders parked at `to_release` for the
 * cashier's branch. Release flips them to completed (minting kit codes + firing
 * commissions) and prints the tape with the codes; Void cancels a queued order
 * with a required reason.
 */
export function ReleasingPage() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const branchesQuery = useQuery({ queryKey: ['branches'], queryFn: listBranches });
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(user?.branchId ?? null);
  useEffect(() => {
    const first = branchesQuery.data?.[0];
    if (!selectedBranchId && first) setSelectedBranchId(first.id);
  }, [branchesQuery.data, selectedBranchId]);
  const branchId = user?.branchId ?? selectedBranchId;

  const queue = useQuery({
    queryKey: ['releasing', branchId],
    queryFn: () => listReleasing(branchId!),
    enabled: !!branchId,
  });

  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['releasing', branchId] });

  const release = useMutation({
    // Release, then re-fetch the order to get the freshly minted codes +
    // branch header for the receipt tape.
    mutationFn: (orderId: string) => releaseOrder(orderId).then(() => getPosOrder(orderId)),
    meta: { successMessage: 'Order released.' },
    onSuccess: (order) => {
      setError(null);
      setReceipt(orderToReceipt(order));
      invalidate();
    },
    onError: (err) => setError(extractError(err)),
  });

  const voidSale = useMutation({
    mutationFn: ({ orderId, reason }: { orderId: string; reason: string }) =>
      voidOrder(orderId, reason),
    meta: { successMessage: 'Order voided.' },
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => setError(extractError(err)),
  });

  const busy = release.isPending || voidSale.isPending;

  if (!branchId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Select a branch</CardTitle>
        </CardHeader>
        <CardContent>
          {branchesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <ul className="space-y-2">
              {branchesQuery.data?.map((b) => (
                <li key={b.id}>
                  <button
                    onClick={() => setSelectedBranchId(b.id)}
                    className="w-full text-left p-3 border rounded-md hover:bg-accent"
                  >
                    <div className="font-medium">{b.name}</div>
                    <div className="text-xs text-muted-foreground">{b.code}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    );
  }

  if (receipt) {
    return <Receipt data={receipt} onClose={() => setReceipt(null)} />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Releasing queue</span>
          {/* A dropdown, not a "Switch branch" link: clearing the selection
              only made the effect above re-select the first branch at once. */}
          <span className="text-base font-normal tracking-normal">
            <BranchPicker
              branches={branchesQuery.data ?? []}
              branchId={branchId}
              isScoped={!!user?.branchId}
              onChange={setSelectedBranchId}
            />
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-destructive mb-3">{prettyError(error)}</p>}
        {queue.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !queue.data?.length ? (
          <p className="text-sm text-muted-foreground">No orders awaiting release.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-2">Order</th>
                <th className="py-2 pr-2">Buyer</th>
                <th className="py-2 pr-2 w-24">Total</th>
                <th className="py-2 pr-2 w-20">Paid</th>
                <th className="py-2 pr-2 w-24">Time</th>
                <th className="py-2 pr-2 w-72">Actions</th>
              </tr>
            </thead>
            <tbody>
              {queue.data.map((o) => (
                <ReleasingRow
                  key={o.id}
                  order={o}
                  busy={busy}
                  onRelease={() => release.mutate(o.id)}
                  onVoid={(reason) => voidSale.mutate({ orderId: o.id, reason })}
                />
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function ReleasingRow({
  order,
  onRelease,
  onVoid,
  busy,
}: {
  order: ReleasingOrder;
  onRelease: () => void;
  onVoid: (reason: string) => void;
  busy: boolean;
}) {
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <tr className="border-b last:border-0 hover:bg-muted/30">
      <td className="py-2 pr-2 font-mono">{order.code}</td>
      <td className="py-2 pr-2">{order.buyerName ?? order.buyerEmail}</td>
      <td className="py-2 pr-2">₱{Number(order.total).toFixed(2)}</td>
      <td className="py-2 pr-2 capitalize text-muted-foreground">
        {order.paymentMethodLabel ?? order.paymentMethod ?? '—'}
      </td>
      <td className="py-2 pr-2 text-muted-foreground">
        {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </td>
      <td className="py-2 pr-2">
        {voiding ? (
          <div className="flex items-center gap-1">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required)"
              className="h-8"
            />
            <Button
              size="sm"
              variant="destructive"
              disabled={!reason.trim() || busy}
              onClick={() => onVoid(reason.trim())}
            >
              Confirm
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setVoiding(false);
                setReason('');
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex gap-1">
            <Button size="sm" disabled={busy} onClick={onRelease}>
              Release
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setVoiding(true)}>
              Void
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}
